// @flow

import * as React from "react"
import { compose } from "react-apollo"
import { FormattedMessage } from "react-intl"
import { connect } from "react-redux"
import { TweenMax, Cubic } from "gsap"
import moment from "moment"
import SVGLoader from "../../UI/SVGLoader"
import { setModal } from "../../Modal"
import TitleIcon from "../Shared/TitleIcon"
import { withCurrentKioskData } from "../queries"
import {
  colorsForLevel,
  levelForValue,
  Legend,
  AqiLevel,
  AqiTitle,
  AqiSubtitle,
  AqiLegendLabel,
  levels,
  Breakpoints
} from "../Shared/AQILegend"
import { MetricLabel } from "../CurrentAirQualityView"

import {
  Titles,
  Title,
  Subtitle,
  Block,
  InfoCol,
  ContentCol,
  ContentText,
  PreviousButton,
  NextButton,
  CurrentStatus,
  StatusDescription,
  StatusUnits
} from "./UI"

import iconPM2_5 from "../Icons/iconMetricPM2_5.svg"
import iconPM10 from "../Icons/iconMetricPM10.svg"
import iconO3 from "../Icons/iconMetricO3.svg"
import iconArrow from "../Icons/iconArrow.svg"

import type { Match, RouterHistory } from "react-router-dom"
import type { SetModal } from "../../Modal"
import type { Metric } from "../Shared/AQILegend"
import type { CurrentKiosk } from "../queries"

import {
  Container,
  Modal,
  Content,
  GlossaryBody,
  PrimaryButton,
  ButtonGroup,
  Fade
} from "../Modal"

type Props = {
  match: Match,
  history: RouterHistory,
  setModal: SetModal,
  data: CurrentKiosk
}

const icons: { [Metric]: string } = {
  pm2_5: iconPM2_5,
  pm10: iconPM10,
  o3: iconO3
}

const metrics: Metric[] = ["pm2_5", "pm10", "o3"]

class GlossaryModal extends React.Component<Props> {
  body: ?HTMLElement
  fade: ?HTMLElement
  legendKeys: Array<HTMLElement>
  titles: Array<HTMLElement>
  arrows: Array<HTMLElement>

  constructor(props) {
    super(props)
    this.body = null
    this.fade = null
    this.legendKeys = []
    this.titles = []
    this.arrows = []
  }

  componentDidMount = () => {
    TweenMax.set(this.body, { y: "-=50", opacity: 0 })
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 1, y: 0 })

    TweenMax.set(this.fade, { opacity: 0 })
    TweenMax.to(this.fade, 0.5, { ease: Cubic.easeOut, opacity: 1 })

    this.animateContent()
  }

  componentDidUpdate = prevProps => {
    if (this.props.match.params.metric !== prevProps.match.params.metric) {
      this.animateContent()
    }
  }

  animateContent = () => {
    const titles = this.titles.filter(title => title !== null)
    const legendKeys = this.legendKeys.filter(title => title !== null)
    TweenMax.set(titles, {
      opacity: 0,
      y: "+=30"
    })
    TweenMax.staggerTo(
      titles,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 0.25 },
      0.25
    )
    TweenMax.set(legendKeys, { opacity: 0, y: "+=50" })
    TweenMax.staggerTo(
      legendKeys,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 1 },
      0.25
    )
  }

  handleClose = () => {
    this.dismiss()
  }

  dismiss = () => {
    const { history, setModal } = this.props
    setModal(false)
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 0, y: "+=50" })
    TweenMax.to(this.fade, 0.5, {
      ease: Cubic.easeOut,
      opacity: 0,
      onComplete: () => {
        history.goBack()
      }
    })
  }

  prevMetric = () => {
    const { match: { params: { metric } } } = this.props
    const index = metrics.indexOf(metric) - 1
    const prevMetric = index >= 0 ? metrics[index] : metrics[metrics.length - 1]
    this.switchMetric(prevMetric)
  }

  nextMetric = () => {
    const { match: { params: { metric } } } = this.props
    const index = metrics.indexOf(metric) + 1
    const nextMetric = index < metrics.length ? metrics[index] : metrics[0]
    this.switchMetric(nextMetric)
  }

  switchMetric = (metric: Metric) => {
    const { history } = this.props
    const path = {
      pathname: `/kiosk/glossary/${metric}`,
      state: {
        modal: true
      }
    }
    history.replace(path)
  }

  render() {
    const { match: { params }, data } = this.props
    const metric: Metric = params.metric ? params.metric : "pm2_5"
    const readingsWithData =
      data.currentKiosk && data.currentKiosk.readings
        ? [...data.currentKiosk.readings.live]
            .sort(
              (a, b) => (moment(a.occurredAt).isBefore(b.occurredAt) ? 1 : -1)
            )
            .map(reading => reading[metric])
            .filter(reading => reading > 0)
        : []

    const earliestReading = Math.round(
      readingsWithData.length > 0
        ? readingsWithData[readingsWithData.length - 1]
        : 0
    )
    const latestReading = Math.round(readingsWithData[0]) || -1
    const diff =
      readingsWithData.length > 0 ? latestReading - earliestReading : 0
    const direction =
      diff === 0 ? "stable" : diff > 0 ? "increasing" : "decreasing"
    const level = levelForValue(readingsWithData[0] || 0)
    const { foreground, background } = colorsForLevel(readingsWithData[0])

    return (
      <Container>
        <Fade
          onClick={this.handleClose}
          dark
          innerRef={el => (this.fade = el)}
        />
        <Modal>
          <GlossaryBody innerRef={el => (this.body = el)}>
            <Content>
              <Block>
                <InfoCol>
                  <TitleIcon innerRef={el => this.titles.push(el)}>
                    <SVGLoader
                      path={icons[metric]}
                      height="100%"
                      width="100%"
                      fill="#fff"
                    />
                  </TitleIcon>
                </InfoCol>
                <ContentCol>
                  <Titles>
                    <Title innerRef={el => this.titles.push(el)}>
                      <FormattedMessage id={`kiosk.glossary.${metric}.title`} />
                    </Title>
                    <Subtitle innerRef={el => this.titles.push(el)}>
                      <FormattedMessage
                        id={`kiosk.glossary.${metric}.subtitle`}
                      />
                    </Subtitle>
                  </Titles>
                  <ContentText innerRef={el => this.titles.push(el)}>
                    <FormattedMessage
                      id={`kiosk.glossary.${metric}.description`}
                    />
                  </ContentText>
                </ContentCol>
              </Block>
              <Block>
                <InfoCol>
                  <Legend compact={true} minHeight={"300px"}>
                    {levels.map(level => (
                      <AqiLevel
                        key={level}
                        level={level}
                        innerRef={el => this.legendKeys.push(el)}
                      >
                        <AqiTitle compact={true}>
                          <FormattedMessage
                            id={`kiosk.history.${level
                              .toLowerCase()
                              .replace(" ", "")}`}
                          />
                        </AqiTitle>
                        <AqiSubtitle compact={true}>
                          {level === "Very High"
                            ? `>=${Breakpoints[level].low}`
                            : `${Breakpoints[level].low}-${Breakpoints[level]
                                .high}`}{" "}
                          <MetricLabel
                            metric={metric}
                            Wrapper={AqiLegendLabel}
                          />
                        </AqiSubtitle>
                      </AqiLevel>
                    ))}
                  </Legend>
                </InfoCol>
                <ContentCol>
                  <CurrentStatus innerRef={el => this.titles.push(el)}>
                    <FormattedMessage id={`kiosk.glossary.${metric}.current`} />
                  </CurrentStatus>
                  <StatusDescription
                    background={background}
                    foreground={foreground}
                    innerRef={el => this.titles.push(el)}
                  >
                    {latestReading > 0 ? latestReading : "No Data"}
                    {latestReading > 0
                      ? [
                          " ",
                          <MetricLabel metric={metric} Wrapper={StatusUnits} />,
                          " ",
                          <FormattedMessage
                            id={`kiosk.forecast.level.${level
                              .toLowerCase()
                              .replace(" ", "")}`}
                          />,
                          " ",
                          <FormattedMessage
                            id={`kiosk.forecast.conjunction`}
                          />,
                          " ",
                          <FormattedMessage
                            id={`kiosk.forecast.direction.${direction}`}
                          />
                        ]
                      : ""}
                  </StatusDescription>
                  <ContentText innerRef={el => this.titles.push(el)}>
                    <FormattedMessage
                      id={`kiosk.glossary.${metric}.status.${level
                        .toLowerCase()
                        .replace(" ", "")}`}
                    />
                  </ContentText>
                </ContentCol>
              </Block>
              <ButtonGroup innerRef={el => this.titles.push(el)}>
                <PrimaryButton onClick={this.handleClose}>
                  <FormattedMessage id="kiosk.history.close" />
                </PrimaryButton>
              </ButtonGroup>
            </Content>
            <PreviousButton onClick={this.prevMetric}>
              <SVGLoader
                path={iconArrow}
                height="100%"
                width="100%"
                fill="#25beae"
              />
            </PreviousButton>
            <NextButton onClick={this.nextMetric}>
              <SVGLoader
                path={iconArrow}
                height="100%"
                width="100%"
                fill="#25beae"
              />
            </NextButton>
          </GlossaryBody>
        </Modal>
      </Container>
    )
  }
}

const ModalWithData = compose(withCurrentKioskData)(GlossaryModal)

export default connect(null, {
  setModal
})(ModalWithData)



// WEBPACK FOOTER //
// ./src/Kiosk/GlossaryModal/GlossaryModal.js